// ------------------------ Common Types ------------------------
export type TimestampedContent = {
  timestamp: Data;
  role?: Data;
  content: Data;
};


export interface CallMetadata {
  agent_name: string;
  starttime: string; // ISO 8601 date string
}

// ------------------------ Servicing Summary Types ------------------------
interface InformationItem {
  information_type: string;
  content: string;
}


export interface IServicingResponse {
  status: Data;
  results: IServicingSummary;
  timing: Timing;
  timestamp: Data;
  __path: string;
}

interface Timing {
  speech_to_text: Timeonhold;
  speaker_identification: Timeonhold;
  service_calls_analysis: Timeonhold;
  __path: string;
}

export interface IServicingSummary {
  parentpath: string;
  call_analysis: Callanalysis;
  metadata: Metadata;
  transcription: Transcription[];
  __path: string;
}

interface Transcription {
  content: Data;
  timestamp: Data;
  role: Data;
  __path: string;
}

interface Metadata {
  agent_name: Data;
  __path: string;
}

interface Callanalysis {
  customer_intent_analysis: Customerintentanalysis;
  call_timeline: Calltimeline;
  future_payment_plans: Futurepaymentplan[];
  call_efficieny: Callefficieny;
  action_items: ActionItems;
  agent_sentiment_analysis: Agentsentimentanalysis;
  customer_issue_resolution_status: Customerissueresolutionstatus;
  inbound_outbound_call: Inboundoutboundcall;
  summary_and_insights: Summaryandinsights;
  customer_objections: Customerobjections;
  resolutions_discussed: Resolutionsdiscussed[];
  customer_sentiment_analysis: Agentsentimentanalysis;
  customer_name: Data;
  call_duration: Timeonhold;
  __path: string;
}

interface Resolutionsdiscussed {
  resolution_discussed: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Customerobjections {
  objections: Objection[];
  __path: string;
}

interface Objection {
  objection: Data;
  response: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Summaryandinsights {
  summary: Data[];
  insights: Data[];
  excerpt: Excerpt;
  __path: string;
}

interface Inboundoutboundcall {
  call_type: Data;
  reasoning: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Customerissueresolutionstatus {
  resolution_status: Resolutionstatus;
  chosen_option: Data;
  explanation: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Resolutionstatus {
  value: boolean;
  __path: string;
}

interface Agentsentimentanalysis {
  overall_sentiment: Overallsentiment;
  beginning: Overallsentiment;
  middle: Overallsentiment;
  end: Overallsentiment;
  __path: string;
}

interface Overallsentiment {
  sentiment_label: Data;
  excerpt: Excerpt;
  __path: string;
}

interface ActionItems {
  customer_actions: ActionItem[];
  agent_actions: ActionItem[];
  __path: string;
}

interface ActionItem {
  action_description: Data;
  purpose: Data;
  deadline: Data;
  excerpt: Excerpt;
  follow_up_details: Data;
  __path: string;
}

interface Callefficieny {
  time_on_hold: Timeonhold;
  number_of_holds: Timeonhold;
  transfers: Timeonhold;
  areas_for_improvement: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Timeonhold {
  value: number;
  __path: string;
}

interface Futurepaymentplan {
  payment_plan_type: Data;
  customer_decision: Data;
  agent_guidance: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Calltimeline {
  events: Event[];
  __path: string;
}

interface Event {
  event_description: Data;
  excerpt: Excerpt;
  __path: string;
}

interface Customerintentanalysis {
  primary_intent: Primaryintent;
  secondary_intent: Primaryintent;
  tertiary_intent: Tertiaryintent;
  __path: string;
}

interface Tertiaryintent {
  value: null;
  __path: string;
}

interface Primaryintent {
  intent: Data;
  reason: Data;
  excerpt: Excerpt;
  category: Data;
  __path: string;
}

interface Excerpt {
  excerpt: Data;
  speaker: Data;
  start_time: Data;
  end_time: Data;
  __path: string;
}

export interface Data {
  value: string;
  __path: string;
}

// ------------------------ Compliance Summary Types ------------------------

// export type ComplianceStatus = 'Pass' | 'Fail' | '';

export type IComplianceBorrower = {
  borrower_number: Data;
  borrower_name: Data;
  ethnicity: DemographicInfo[];
  race: DemographicInfo[];
  sex: DemographicInfo;
};

export interface IComplianceSummary {
  call_metadata: CallMetadata;
  demographics: Demographics;
  script_adherence: IComplianceScriptAdherence;
  transcription: TimestampedContent[];
  additional_information?: InformationItem[];
}

export interface Demographics {
  demographics_overall_pass_or_fail: {
    value: string;
    __path: string;
  };
  demographics_overall_pass_or_fail_reason: {
    value: string;
    __path: string;
  };
  borrowers: IComplianceBorrower[];
}

export interface DemographicInfo {
  voice: Data;
  form_1003: Data;
  pass_or_fail: Data;
  excerpt: Data;
}

export interface ComplianceScriptAdherenceItem {
  pass_or_fail: {
    value: string;
    __path: string;
  };
  excerpt: Data;
  reason: Data;
}

export interface IComplianceScriptAdherence {
  script_adherence_overall_pass_or_fail: {
    value: string;
    __path: string;
  };
  script_adherence_overall_pass_or_fail_reason: {
    value: string;
    __path: string;
  };
  disclosure_statement: ComplianceScriptAdherenceItem;
  ethnicity: ComplianceScriptAdherenceItem[];
  race: ComplianceScriptAdherenceItem[];
  sex: ComplianceScriptAdherenceItem;
}

export enum EComplianceBorrowerFieldKey {
  NAME = 'name',
  ETHNICITY = 'ethnicity',
  RACE = 'race',
  SEX = 'sex',
}

// ------------------------ Union Type ------------------------
export type TSummary = IComplianceSummary | IServicingSummary;
